import { Component } from '@theme/component';
import { readGridDepth, saveGridDepthOnLeave } from '@ts/grid-state';

/**
 * "View more" pagination for the blog listing section. The button is a real
 * link to the next paginated page (works without JS); this component
 * intercepts the click, fetches that page's section markup via the Section
 * Rendering API, and appends its rows in place.
 *
 * The loaded depth persists across a navigation away (see @ts/grid-state):
 * returning via the back button re-appends the pages the shopper had loaded,
 * and the browser restores their scroll position once the rows are back.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rows - Row container that receives the appended rows.
 * @property {HTMLElement} [range] - Cumulative "1-X of Y" progress line.
 * @property {HTMLElement} [viewMore] - "View more" wrapper (absent on the last page).
 * @property {HTMLAnchorElement} [viewMoreLink] - Next-page anchor inside the wrapper.
 *
 * @extends {Component<Refs>}
 */
class TSBlogPostsList extends Component {
  /** @type {boolean} */
  #loading = false;

  /** @type {number} Pages currently rendered (the server-rendered page plus appended ones) */
  #pagesLoaded = 1;

  /** @type {(() => void) | null} */
  #removeDepthSaver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#removeDepthSaver = saveGridDepthOnLeave(this.#depthId(), () => this.#pagesLoaded);
    this.#restoreDepth();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDepthSaver?.();
  }

  /** @returns {string} Namespaces stored depth per section instance. */
  #depthId() {
    return `blog-posts:${this.dataset.sectionId ?? ''}`;
  }

  /**
   * Click handler bound via `on:click="/loadMore"`. Falls back to normal
   * navigation when the fetch fails.
   *
   * @param {MouseEvent} event
   * @returns {Promise<void>}
   */
  async loadMore(event) {
    event.preventDefault();

    const link = this.refs.viewMoreLink;
    if (!link || this.#loading) return;

    this.#loading = true;
    link.setAttribute('aria-disabled', 'true');
    this.refs.rows.setAttribute('aria-busy', 'true');

    try {
      this.#appendPageDoc(await this.#fetchPageDoc(link.href), { moveFocus: true });
    } catch (error) {
      console.error('Blog posts view more error:', error);
      window.location.assign(link.href);
    } finally {
      this.#loading = false;
      this.refs.rows.removeAttribute('aria-busy');
      this.refs.viewMoreLink?.removeAttribute('aria-disabled');
    }
  }

  /**
   * Returning via the back button: re-append the pages the shopper had loaded.
   * Each page's markup carries the next page's link, so they load in sequence —
   * a failure keeps whatever already appended. The browser restores the scroll
   * position itself once the rows are back.
   */
  async #restoreDepth() {
    const depth = readGridDepth(this.#depthId());
    if (!depth) return;

    this.#loading = true;
    this.refs.rows.setAttribute('aria-busy', 'true');

    try {
      // Driven by each page's own "next" href rather than the live ref, which
      // the Component base class only refreshes asynchronously.
      /** @type {string | null | undefined} */
      let nextHref = this.refs.viewMoreLink?.href;

      while (this.#pagesLoaded < depth && nextHref) {
        nextHref = this.#appendPageDoc(await this.#fetchPageDoc(nextHref));
      }
    } catch (error) {
      console.error('Blog posts restore error:', error);
    } finally {
      this.#loading = false;
      this.refs.rows.removeAttribute('aria-busy');
    }
  }

  /**
   * Fetch a paginated page's markup for this section via the Section Rendering
   * API.
   *
   * @param {string} href
   * @returns {Promise<Document>}
   */
  async #fetchPageDoc(href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId ?? '');

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * Merge a fetched page's section markup into the list: append its rows and
   * adopt its server-computed progress line and "View more" state.
   *
   * @param {Document} doc
   * @param {{ moveFocus?: boolean }} [options] - Move focus into the appended
   *   content when the button disappears with the last page. Interactive clicks
   *   only — focus must not jump during a back-navigation restore.
   * @returns {string | null} The next page's href, or null on the last page.
   */
  #appendPageDoc(doc, { moveFocus = false } = {}) {
    const newRows = doc.querySelector('[ref="rows"]');
    if (!newRows) {
      throw new Error('Next page markup is missing the rows container');
    }

    const link = this.refs.viewMoreLink;
    const newLink = doc.querySelector('[ref="viewMoreLink"]');
    const newRange = doc.querySelector('[ref="range"]');

    const appended = [...newRows.children];
    this.refs.rows.append(...appended);
    this.#pagesLoaded += 1;

    // The fetched page's progress line and button label are server-computed
    // for its cumulative position ("1-20 of 80", "View 10 more").
    if (this.refs.range && newRange) {
      this.refs.range.textContent = newRange.textContent;
    }

    if (link && newLink?.getAttribute('href')) {
      link.href = newLink.getAttribute('href') ?? '';

      const label = link.querySelector('.ts-button__label');
      const newLabel = newLink.querySelector('.ts-button__label');

      if (label && newLabel) {
        label.textContent = newLabel.textContent;
      }

      return link.href;
    }

    // Last page: the focused button is about to disappear, so hand focus
    // to the first visible link of the newly appended content.
    const firstFocusable = appended[0]?.querySelector('a[href]:not([tabindex="-1"]):not([aria-hidden="true"]), button');
    this.refs.viewMore?.remove();

    if (moveFocus && firstFocusable instanceof HTMLElement) {
      firstFocusable.focus();
    }

    return null;
  }
}

if (!customElements.get('ts-blog-posts-list')) {
  customElements.define('ts-blog-posts-list', TSBlogPostsList);
}
